import SpeedTest from '@cloudflare/speedtest';
import { axiosInstance } from '@/api/axiosInstance';

export const DEVICE_ID_STORAGE_KEY = 'corpus_client_device_id';
const SPEED_TEST_TIMEOUT_MS = 12_000;

type NullableNumber = number | null;

export interface NetworkSpeedResult {
  download_speed_mbps: NullableNumber;
  upload_speed_mbps: NullableNumber;
}

export interface DeviceProfilePayload extends NetworkSpeedResult {
  device_id: string;
  fingerprint_hash: string;
  device_name: string;
  os_type: string;
  os_name: string;
  os_version: string;
  cpu_model: string | null;
  cpu_architecture: string | null;
  cpu_cores: number | null;
  ram_gb: NullableNumber;
  gpu_model: string | null;
}

interface UserAgentHighEntropyValues {
  architecture?: string;
  bitness?: string;
  model?: string;
  platform?: string;
  platformVersion?: string;
}

interface NavigatorUAData {
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (
    hints: string[],
  ) => Promise<UserAgentHighEntropyValues>;
}

interface NavigatorWithDeviceHints extends Navigator {
  deviceMemory?: number;
  userAgentData?: NavigatorUAData;
  gpu?: {
    requestAdapter?: () => Promise<{
      info?: {
        vendor?: string;
        architecture?: string;
        device?: string;
        description?: string;
      };
    } | null>;
  };
}

type SpeedTestInstance = InstanceType<typeof SpeedTest>;
type SpeedTestConfig = NonNullable<ConstructorParameters<typeof SpeedTest>[0]>;

const SPEED_TEST_CONFIG: SpeedTestConfig = {
  autoStart: false,
  measurements: [
    { type: 'download', bytes: 100_000, count: 1, bypassMinDuration: true },
    { type: 'upload', bytes: 100_000, count: 1, bypassMinDuration: true },
  ],
  measureDownloadLoadedLatency: false,
  measureUploadLoadedLatency: false,
} as const;

function toMbps(bitsPerSecond: number | undefined): NullableNumber {
  return typeof bitsPerSecond === 'number' && Number.isFinite(bitsPerSecond)
    ? bitsPerSecond / 1_000_000
    : null;
}

function readSpeedMbps(test: SpeedTestInstance): NetworkSpeedResult {
  const results = test.results as SpeedTestInstance['results'] & {
    getUploadBandwidth?: () => number | undefined;
  };
  const summary = results.getSummary() as {
    download?: number;
    upload?: number;
  };

  return {
    download_speed_mbps:
      toMbps(summary.download) ?? toMbps(results.getDownloadBandwidth()),
    upload_speed_mbps:
      toMbps(summary.upload) ?? toMbps(results.getUploadBandwidth?.()),
  };
}

export async function probeNetworkSpeed(): Promise<NetworkSpeedResult> {
  if (typeof window === 'undefined' || !navigator.onLine) {
    return { download_speed_mbps: null, upload_speed_mbps: null };
  }

  return new Promise((resolve) => {
    const test = new SpeedTest(SPEED_TEST_CONFIG);
    let settled = false;

    const settle = (result: NetworkSpeedResult) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      test.pause();
      resolve(result);
    };

    const timeoutId = window.setTimeout(() => {
      settle({ download_speed_mbps: null, upload_speed_mbps: null });
    }, SPEED_TEST_TIMEOUT_MS);

    test.onFinish = () => settle(readSpeedMbps(test));
    test.onError = () =>
      settle({ download_speed_mbps: null, upload_speed_mbps: null });

    try {
      test.play();
    } catch {
      settle({ download_speed_mbps: null, upload_speed_mbps: null });
    }
  });
}

function createDeviceId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function getOrCreateDeviceId(storage = localStorage): string {
  const existing = storage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing) return existing;

  const next = createDeviceId();
  storage.setItem(DEVICE_ID_STORAGE_KEY, next);
  return next;
}

function fallbackHash(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return `fallback-${Math.abs(hash).toString(16)}`;
}

export async function createFingerprintHash(parts: string[]): Promise<string> {
  const input = parts.filter(Boolean).join('|');
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    return fallbackHash(input);
  }

  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function inferOsType(platform: string, userAgent: string): string {
  const value = `${platform} ${userAgent}`.toLowerCase();
  if (value.includes('android')) return 'android';
  if (
    value.includes('iphone') ||
    value.includes('ipad') ||
    value.includes('ios')
  ) {
    return 'ios';
  }
  if (value.includes('mac')) return 'macos';
  if (value.includes('win')) return 'windows';
  if (value.includes('linux')) return 'linux';
  return 'web';
}

function inferOsName(osType: string, platform: string): string {
  if (platform) return platform;
  return osType === 'web' ? 'Unknown' : osType;
}

function formatCpuArchitecture(
  values: UserAgentHighEntropyValues,
): string | null {
  const parts = [values.architecture, values.bitness].filter(Boolean);
  return parts.length > 0 ? parts.join('-') : null;
}

async function getHighEntropyValues(
  nav: NavigatorWithDeviceHints,
): Promise<UserAgentHighEntropyValues> {
  if (!nav.userAgentData?.getHighEntropyValues) return {};
  try {
    return await nav.userAgentData.getHighEntropyValues([
      'architecture',
      'bitness',
      'model',
      'platform',
      'platformVersion',
    ]);
  } catch {
    return {};
  }
}

async function getGpuModel(
  nav: NavigatorWithDeviceHints,
): Promise<string | null> {
  if (!nav.gpu?.requestAdapter) return null;
  try {
    const adapter = await nav.gpu.requestAdapter();
    const info = adapter?.info;
    if (!info) return null;
    return (
      info.description ||
      [info.vendor, info.architecture, info.device].filter(Boolean).join(' ') ||
      null
    );
  } catch {
    return null;
  }
}

export async function collectDeviceProfile(
  deviceId = getOrCreateDeviceId(),
  networkSpeed?: NetworkSpeedResult,
): Promise<DeviceProfilePayload> {
  const nav = navigator as NavigatorWithDeviceHints;
  const highEntropy = await getHighEntropyValues(nav);
  const gpuModel = await getGpuModel(nav);
  const speed = networkSpeed ?? (await probeNetworkSpeed());
  const platform =
    highEntropy.platform || nav.userAgentData?.platform || nav.platform || '';
  const osType = inferOsType(platform, nav.userAgent || '');
  const deviceName =
    highEntropy.model || `${inferOsName(osType, platform)} ${osType}`.trim();
  const cpuArchitecture = formatCpuArchitecture(highEntropy);
  const fingerprintHash = await createFingerprintHash([
    deviceId,
    nav.userAgent || '',
    platform,
    cpuArchitecture || '',
  ]);

  return {
    device_id: deviceId,
    fingerprint_hash: fingerprintHash,
    device_name: deviceName,
    os_type: osType,
    os_name: inferOsName(osType, platform),
    os_version: highEntropy.platformVersion || '',
    cpu_model: null,
    cpu_architecture: cpuArchitecture,
    cpu_cores:
      typeof nav.hardwareConcurrency === 'number'
        ? nav.hardwareConcurrency
        : null,
    ram_gb: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null,
    gpu_model: gpuModel,
    download_speed_mbps: speed.download_speed_mbps,
    upload_speed_mbps: speed.upload_speed_mbps,
  };
}

export async function registerDeviceProfile(
  payload: DeviceProfilePayload,
  token?: string,
): Promise<void> {
  await axiosInstance.put(
    '/devices/',
    payload,
    token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : undefined,
  );
}

export async function collectAndRegisterDeviceProfile(
  token?: string,
): Promise<DeviceProfilePayload> {
  const payload = await collectDeviceProfile();
  await registerDeviceProfile(payload, token);
  return payload;
}
